import { useTypedSelector } from '../../useTypedSelector'
import { selectUserIdExtra } from '../../../store/reducers/user/selectors'
import React, { useEffect, useState } from 'react'
import api from '../../../api'
import {
	createSearchRegex,
	executeResponseError,
	getNameOrMail,
} from '../../../helpers'
import { ChatChannel, MessageProps } from '../../../types/chat'
import { history } from '../../../store'
import { PAGE_CHAT } from '../../../constants/navigation'
import { Message } from '@twilio/conversations'
import useIsMounted from '../../useIsMounted'
import { selectPatientID } from '../../../store/reducers/patientOverview/coachSystemData/selectors'
import { selectTiers } from '../../../store/reducers/patientOverview/selectors'

export type ChannelPatientData = { [sid: string]: ChatChannel['patientData'] }

interface UseCHatMessagesProps {
	setSearchValue: (value: string) => void
	fetchUserChatToken: () => Promise<string>
	token: string
	channels: ChatChannel[]
	allChannels: ChatChannel[]
	channelKeys: ChannelPatientData
	isChat: boolean
	getChannelByPatientId: (userId: string) => Promise<ChatChannel>
	channelsAreLoading: boolean
	getPatientChat: (coachId: string) => Promise<string>
	patientChat?: ChatChannel
	setPinnedChannelsSids: React.Dispatch<React.SetStateAction<string[]>>
	pinnedChannelsSids: string[]
	searchValue: string
}

const PINNED_CHANNELS_LOCAL_STORAGE_KEY = 'pinned_chat_channels'

export const getUniqueId = (uniqueName: string | null): string => {
	if (uniqueName) {
		const id = uniqueName.split('_')
		return id[1]
	}
	return ''
}

export const formatMessage = (message: Message): MessageProps => {
	const uniqueName = getUniqueId(message?.conversation?.uniqueName)
	const { sid, author, body, dateCreated, index, conversation: { friendlyName } } = message
	const isOutgoing = uniqueName === author
	return { sid, author, body, dateCreated, isOutgoing, index, friendlyName }
}

const setPinnedStatusMapCallback =
	(initialPinnedChannelsSids: string[]) => (channel: ChatChannel) => ({
		...channel,
		pinned: initialPinnedChannelsSids.includes(channel.channel_sid),
	})

const useChatHelper = (): UseCHatMessagesProps => {
	const userId = useTypedSelector(selectUserIdExtra)
	const isMounted = useIsMounted()
	const pinnedSidsFromLS = localStorage.getItem(
		PINNED_CHANNELS_LOCAL_STORAGE_KEY
	)
	const [token, setToken] = React.useState('')
	const [searchValue, setSearchValue] = React.useState('')
	const [allChannels, setAllChannels] = React.useState<ChatChannel[]>([])
	const [channelKeys, setChannelKeys] = React.useState<ChannelPatientData>({})
	const [channels, setChannels] = React.useState<ChatChannel[]>([])
	const [channelsAreLoading, setChannelsAreLoading] = useState<boolean>(true)
	const [patientChat, setPatientChat] = React.useState<ChatChannel>()
	const [failedChatIds, setFailedChatIds] = React.useState<string[]>([])
	const tires = useTypedSelector(selectTiers)
	const [pinnedChannelsSids, setPinnedChannelsSids] = useState<string[]>(
		pinnedSidsFromLS ? JSON.parse(pinnedSidsFromLS) : []
	)
	const isChat = history.location.pathname.includes(PAGE_CHAT)
	const patientID = useTypedSelector(selectPatientID)

	const fetchUserChatToken = async (): Promise<string> => {
		try {
			const res = await api.chat.getUserChatToken(userId)
			if (res?.data?.result) {
				setToken(res?.data?.result)
				return res?.data?.result
			}
			return ''
		} catch (e) {
			return ''
		}
	}

	const getPatientChat = async (coachId: string): Promise<string> => {
		if (!coachId) return ''

		try {
			const res = await api.chat.getPatientChat(patientID, coachId)
			if (res?.data?.result) {
				setPatientChat(res?.data?.result)
				return res.data.result?.channel_sid
			}
			return ''
		} catch (e) {
			setFailedChatIds(ids => [...ids, coachId])
			return ''
		}
	}

	const getAllUserChannels = async (): Promise<void> => {
		try {
			const res = await api.chat.getAllUserChannels()
			if (res?.data?.result) {
				const chan = res?.data?.result
				const channelsData = chan.reduce(
					(acc: ChannelPatientData, item: ChatChannel) => {
						acc[item.channel_sid] = item.patientData
						return acc
					},
					{}
				)

				setAllChannels(
					chan.map(setPinnedStatusMapCallback(pinnedChannelsSids))
				)
				setChannelKeys(channelsData)
			}
		} catch (e) {
			const error = executeResponseError(e, 'Get users chats error')

			throw new Error(error || '')
		} finally {
			isMounted() && setChannelsAreLoading(false)
		}
	}

	const getChannelByPatientId = async (
		userId: string
	): Promise<ChatChannel> => {
		try {
			const res = await api.chat.getAllUserChannels()
			const channels = res?.data?.result
			return channels.find(
				(c: ChatChannel) => c.provisioning_id === userId
			)
		} catch (e) {
			const error = executeResponseError(e, 'Get chat with patient error')
			throw new Error(error || '')
		}
	}

	const filterChannels = (): void => {
		if (!searchValue) {
			setChannels(
				allChannels.map(setPinnedStatusMapCallback(pinnedChannelsSids))
			)

			return
		}
		const searchingRegex = createSearchRegex(searchValue)
		const foundedChannels = allChannels.filter(ch => {
			const { firstName, lastName, email } = ch.patientData
			const name = getNameOrMail(firstName, lastName, email)

			return name.toLowerCase().match(searchingRegex)
		})

		setChannels(
			foundedChannels.map(setPinnedStatusMapCallback(pinnedChannelsSids))
		)
	}

	const getNextTireChat = async (): Promise<void> => {
		for await (const tire of tires) {
			if (
				tire?._id &&
				!failedChatIds.includes(tire._id || '') &&
				tire.coachId
			) {
				const res = await getPatientChat(tire.coachId || '')

				if (!res) {
					break
				}
			}
		}
	}

	useEffect(() => {
		setFailedChatIds([])
	}, [patientID])

	useEffect(() => {
		if (failedChatIds.length && tires.length) {
			getNextTireChat()
		}
	}, [failedChatIds.length])

	useEffect(() => {
		localStorage.setItem(
			PINNED_CHANNELS_LOCAL_STORAGE_KEY,
			JSON.stringify(pinnedChannelsSids)
		)

		setChannels(channels => {
			return [...channels].map(
				setPinnedStatusMapCallback(pinnedChannelsSids)
			)
		})
	}, [pinnedChannelsSids])

	useEffect(() => {
		if (allChannels.length) {
			filterChannels()
		}
	}, [searchValue, allChannels.length])

	useEffect(() => {
		if (userId) {
			fetchUserChatToken().then(getAllUserChannels)
		}
	}, [userId])

	return {
		setSearchValue,
		fetchUserChatToken,
		getPatientChat,
		patientChat,
		channelKeys,
		token,
		channels,
		isChat,
		channelsAreLoading,
		getChannelByPatientId,
		setPinnedChannelsSids,
		pinnedChannelsSids,
		allChannels,
		searchValue,
	}
}

export default useChatHelper
